import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
  subLabel?: string;
  icon?: React.ElementType;
  isDefault?: boolean;
}

interface SelectProps {
  label?: string;
  name?: string;
  options: Option[];
  className?: string;
  onChange: (selectedValue: string) => void;
  value?: string;
  displayValue?: (value: string) => string;
  error?: string;
  message?: string;
  icon?: any; // <-- Added icon prop for label icon
  placeholder?: string;
}

export default function Select({
  label,
  options,
  className,
  placeholder,
  onChange,
  value,
  displayValue,
  error,
  message,
  icon // Destructure label icon
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [, setSelectedValue] = useState(value || "");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<React.ReactNode>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // A field near the bottom of a scrollable form (e.g. "Assign to" as the
  // last field in the ticket modal) otherwise opens its option list below
  // the fold, forcing a scroll to see it — flip it upward instead whenever
  // there isn't enough room below but there's more room above.
  const toggleDropdown = () => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const estimatedHeight = 200; // ~max-h-40 option list + padding
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < estimatedHeight && spaceAbove > spaceBelow);
    }
    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (option: Option) => {
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    setSelectedIcon(
      option.icon ? <option.icon className="text-green-500" /> : null
    );
    onChange(option.value);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update selection when value changes externally
  useEffect(() => {
    const selected = options.find((option) => option.value === value);
    setSelectedValue(value || "");
    setSelectedLabel(selected?.label || "");
    setSelectedIcon(
      selected?.icon ? <selected.icon className="text-green-500" /> : null
    );
  }, [value, options]);

  const displayedValue = displayValue
    ? displayValue(value || "")
    : selectedLabel || (value ? selectedLabel : placeholder || `Select ${label || 'option'}`);

  return (
    <div
      className={`relative flex flex-col  gap-2 md:gap-3 ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {icon && (
        <span className="absolute left-3 top-[55px] -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500">
          {icon}
        </span>
      )}

      <div className="relative">
        <div
          onClick={toggleDropdown}
          className="text-field w-full h-[45px] bg-white dark:bg-transparent border border-[#12121233] dark:border-white px-4 rounded outline-none text-[12px] md:text-[14px] transition gradient-ring flex items-center justify-between cursor-pointer"
        >
          <span className="text-[12px] md:text-[14px] text-[#333333] dark:text-white font-medium flex items-center gap-2 truncate">
            {selectedIcon && (
              <span className="w-4 h-4 flex items-center justify-center shrink-0">
                {selectedIcon}
              </span>
            )}

            <span className="truncate">
              {displayedValue || placeholder || `Select ${label || 'option'}`}
            </span>
          </span>

          <ChevronDown
            size={18}
            className={`text-[#667085] dark:text-gray-300 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""
              }`}
          />
        </div>

        {isOpen && (
          <ul
            className={`absolute z-10 w-full ${openUpward ? "bottom-full mb-1" : "mt-1"} bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 py-3 rounded-md shadow-lg shadow-[#03071214]  max-h-40 overflow-y-auto text-[12px] md:text-[12px]`}>
            {options?.map((option) => {
              const IconComponent = option.icon;

              return (
                <li
                  key={option.value}
                  onClick={() => handleOptionClick(option)}
                  className="flex items-center justify-between py-3 px-5 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    {IconComponent && (
                      <span className="w-4 h-4 flex items-center justify-center">
                        <IconComponent className="text-green-500" />
                      </span>
                    )}

                    <div className="flex flex-col">
                      <span className="text-[14px] font-medium text-[#333333] dark:text-gray-100">
                        {option.label}
                      </span>
                      {option.subLabel && (
                        <span className="text-[12px] text-[#667085] dark:text-gray-400">
                          {option.subLabel}
                        </span>
                      )}
                    </div>
                  </div>


                </li>
              );
            })}

            {message && (
              <div className="mt-1 text-sm text-[#667085] dark:text-gray-400 border-t border-gray-300 dark:border-gray-700 py-5 px-8">
                {message}
              </div>
            )}
          </ul>
        )}
      </div>

      {error && (
        <span className="error-text text-[#FF0101] text-[14px]">{error}</span>
      )}
    </div>
  );
}
